import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';

import { Container } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import SectionNavigation from '~/components/Common/Elements/SectionNavigation';
import SectionNavigationLayout from '~/components/Common/Elements/SectionNavigationLayout';
import { preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';

import { isAdmin } from '~/utils/adminPermission';
import featureToggle from '~/utils/featureToggle';

import MakeMeAnOfferButton from '../../components/MakeMeAnOffer/MakeMeAnOfferButton';
import AccommodationProperties from '../Properties/AccommodationProperties';

export enum AccommodationTabs {
  PROPERTIES = 'properties',
  UNMAPPED_SUPPLIERS = 'unmapped_suppliers',
  POTENTIAL_MAPPINGS = 'potential_mappings',
  FACILITIES = 'facilities',
  AMENITIES = 'amenities',
  INCLUSIONS = 'inclusions',
  MMAO_OFFERS = 'mmao_offers',
  CAMPAIGNS = 'campaigns',
  BUNDLES = 'bundles',
  DYNAMIC_CONFIGURATIONS = 'dynamic_configurations',
  FEATURE_ROLLOUT = 'feature_rollout',
}

preRegisterSpotlightRoutes([
  {
    path: `/accommodation/properties#${AccommodationTabs.PROPERTIES}`,
    label: 'Accommodation / Properties',
    section: 'Accommodation',
  },
  {
    path: `/accommodation/properties#${AccommodationTabs.POTENTIAL_MAPPINGS}`,
    label: 'Accommodation / Potential Mappings',
    section: 'Accommodation',
  },
  {
    path: `/accommodation/properties#${AccommodationTabs.UNMAPPED_SUPPLIERS}`,
    label: 'Accommodation / Unmapped Suppliers',
    section: 'Accommodation',
  },
  {
    path: `/accommodation/properties#${AccommodationTabs.FACILITIES}`,
    label: 'Accommodation / Facilities',
    section: 'Accommodation',
  },
  {
    path: `/accommodation/properties#${AccommodationTabs.AMENITIES}`,
    label: 'Accommodation / Amenities',
    section: 'Accommodation',
  },
  {
    path: `/accommodation/properties#${AccommodationTabs.INCLUSIONS}`,
    label: 'Accommodation / Inclusions',
    section: 'Accommodation',
  },
  {
    path: `/accommodation/properties#${AccommodationTabs.FEATURE_ROLLOUT}`,
    label: 'Accommodation / Feature Rollout',
    section: 'Accommodation',
  },
]);

export default function AccommodationHomePage() {
  const [selectedTab, setSelectedTab] = useState(
    (window.location.hash?.replace('#', '') as AccommodationTabs) ?? AccommodationTabs.PROPERTIES,
  );
  const { push } = useAppNavigation();

  const { user } = useCurrentUser();
  const userIsAdmin = isAdmin(user);
  const isMmaoEnabled = featureToggle.availableToShow('MAKE_ME_AN_OFFER_ENABLED');
  const isMakeMeAnOfferButtonVisible = isMmaoEnabled;

  useEffect(() => {
    if (window.location.hash?.replace('#', '') === '') {
      window.location.hash = AccommodationTabs.PROPERTIES;
      setSelectedTab(AccommodationTabs.PROPERTIES);
    }
  }, []);

  useEffect(() => {
    if (!userIsAdmin && selectedTab === AccommodationTabs.DYNAMIC_CONFIGURATIONS) {
      setSelectedTab(AccommodationTabs.PROPERTIES);
      window.location.hash = AccommodationTabs.PROPERTIES;
    }
  }, [selectedTab, userIsAdmin]);

  const handleTabChange = useCallback(
    (event: SyntheticEvent, newValue: AccommodationTabs) => {
      setSelectedTab(newValue);
      push({
        search: '',
      });
      window.location.hash = newValue;
    },
    [push],
  );

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Accommodation - Properties</title>
      </Helmet>
      <PageHeader title="Accommodation - Properties">
        {isMakeMeAnOfferButtonVisible && <MakeMeAnOfferButton />}
      </PageHeader>
      <SectionNavigationLayout
        navigation={
          <SectionNavigation
            label="Accommodation sections"
            currentTab={selectedTab}
            groups={[
              {
                items: [
                  {
                    id: AccommodationTabs.PROPERTIES,
                    label: 'Properties',

                    href: `/accommodation/properties#${AccommodationTabs.PROPERTIES}`,
                  },
                  {
                    id: AccommodationTabs.POTENTIAL_MAPPINGS,
                    label: 'Potential Mappings',

                    href: `/accommodation/properties#${AccommodationTabs.POTENTIAL_MAPPINGS}`,
                  },
                  {
                    id: AccommodationTabs.UNMAPPED_SUPPLIERS,
                    label: 'Unmapped Suppliers',

                    href: `/accommodation/properties#${AccommodationTabs.UNMAPPED_SUPPLIERS}`,
                  },
                  {
                    id: AccommodationTabs.FACILITIES,
                    label: 'Facilities',

                    href: `/accommodation/properties#${AccommodationTabs.FACILITIES}`,
                  },
                  {
                    id: AccommodationTabs.AMENITIES,
                    label: 'Amenities',

                    href: `/accommodation/properties#${AccommodationTabs.AMENITIES}`,
                  },
                  {
                    id: AccommodationTabs.INCLUSIONS,
                    label: 'Inclusions',

                    href: `/accommodation/properties#${AccommodationTabs.INCLUSIONS}`,
                  },
                  {
                    id: AccommodationTabs.MMAO_OFFERS,
                    label: 'MMAO Offers',

                    href: `/accommodation/properties#${AccommodationTabs.MMAO_OFFERS}`,
                  },
                  {
                    id: AccommodationTabs.CAMPAIGNS,
                    label: 'Campaigns',

                    href: `/accommodation/properties#${AccommodationTabs.CAMPAIGNS}`,
                  },
                  {
                    id: AccommodationTabs.BUNDLES,
                    label: 'Bundles',

                    href: `/accommodation/properties#${AccommodationTabs.BUNDLES}`,
                  },
                  {
                    id: AccommodationTabs.DYNAMIC_CONFIGURATIONS,
                    label: 'Dynamic Configurations',

                    href: `/accommodation/properties#${AccommodationTabs.DYNAMIC_CONFIGURATIONS}`,
                  },
                  {
                    id: AccommodationTabs.FEATURE_ROLLOUT,
                    label: 'Feature Rollout',

                    href: `/accommodation/properties#${AccommodationTabs.FEATURE_ROLLOUT}`,
                  },
                ].filter(
                  (item) =>
                    (item.id !== AccommodationTabs.MMAO_OFFERS || isMmaoEnabled) &&
                    (item.id !== AccommodationTabs.DYNAMIC_CONFIGURATIONS || userIsAdmin),
                ),
              },
            ]}
            onSelect={(event, id) => {
              event.preventDefault();
              handleTabChange(event, id as AccommodationTabs);
            }}
          />
        }
      >
        <AccommodationProperties selectedTab={selectedTab} />
      </SectionNavigationLayout>
    </Container>
  );
}
